import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, KeyboardEvent, useEffect, useId, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { fetchCompanies, fetchCompanySearch } from "../lib/api";
import { fetchCompanyWorkspace } from "../lib/companyWorkspace";
import type { CompanySearchResult } from "../lib/types";

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

type CompanyReference = {
  issuer_id?: string | null;
  ticker?: string | null;
  provider_display_ticker?: string | null;
  company_url?: string | null;
};

function companyRef(company: CompanyReference): string {
  if (company.issuer_id) return company.issuer_id;
  const route = company.company_url?.startsWith("/app/")
    ? company.company_url.slice(4)
    : company.company_url;
  const match = route?.match(/^\/companies\/([^/?#]+)/);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  return company.ticker || company.provider_display_ticker || "";
}

function companyPath(company: CompanyReference): string {
  return `/companies/${encodeURIComponent(companyRef(company))}`;
}

function displayTicker(company: CompanySearchResult): string {
  return company.ticker || company.provider_display_ticker || "—";
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(parsed);
}

export function CompanyLookup() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const listboxId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const normalizedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(normalizedQuery, 200);
  const isDebouncing = normalizedQuery !== debouncedQuery;
  const search = useQuery({
    queryKey: ["company-search", debouncedQuery],
    queryFn: ({ signal }) => fetchCompanySearch({ q: debouncedQuery, limit: 10 }, signal),
    enabled: debouncedQuery.length > 0,
    staleTime: 60_000,
    retry: false,
  });
  const results = useMemo(
    () => isDebouncing ? [] : search.data?.results ?? [],
    [isDebouncing, search.data?.results],
  );
  const showPanel = open && normalizedQuery.length > 0;
  const providerUnavailable = !isDebouncing && search.data?.provider_status?.available === false;

  useEffect(() => {
    const company = results[activeIndex];
    if (!company) return;
    const identifier = companyRef(company);
    void queryClient.prefetchQuery({
      queryKey: ["company-workspace", identifier],
      queryFn: ({ signal }) => fetchCompanyWorkspace(identifier, signal),
      staleTime: 60_000,
    });
  }, [activeIndex, queryClient, results]);

  function select(company: CompanySearchResult) {
    setOpen(false);
    void navigate(companyPath(company));
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      if (results.length) setActiveIndex((current) => Math.min(current + 1, results.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      if (results.length) setActiveIndex((current) => current <= 0 ? results.length - 1 : current - 1);
    } else if (event.key === "Enter" && showPanel && results.length) {
      event.preventDefault();
      select(results[activeIndex >= 0 ? activeIndex : 0]);
    } else if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <section className="company-lookup" aria-labelledby="company-lookup-label">
      <label id="company-lookup-label" htmlFor="company-lookup-input">Find any company</label>
      <div
        className="company-combobox"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
        }}
      >
        <input
          id="company-lookup-input"
          type="search"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
          value={query}
          placeholder="Search ticker or company name"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={showPanel}
          aria-activedescendant={activeIndex >= 0 && results[activeIndex] ? `${listboxId}-${activeIndex}` : undefined}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={onKeyDown}
        />
        {showPanel ? (
          <div className="company-search-panel">
            {isDebouncing || search.isFetching ? <div className="company-lookup-status" role="status">Searching…</div> : null}
            {search.isError && !isDebouncing ? (
              <div className="company-lookup-status company-search-error error-text" role="alert">
                <span>Company search is temporarily unavailable.</span>
                <button className="btn small" type="button" onClick={() => void search.refetch()}>Retry search</button>
              </div>
            ) : null}
            {providerUnavailable && search.data?.count !== 0 ? (
              <div className="company-lookup-status company-directory-warning" role="status">
                Global directory unavailable; showing Athena matches only.
              </div>
            ) : null}
            {!isDebouncing && !search.isFetching && !search.isError && search.data?.count === 0 ? (
              <div className="company-lookup-status" role="status">
                {providerUnavailable
                  ? "Global directory unavailable; showing Athena matches only. No Athena directory matches this search."
                  : "No companies found."}
              </div>
            ) : null}
            {results.length ? (
              <div id={listboxId} className="company-search-results" role="listbox" aria-label="Company search results">
                {results.map((company, index) => {
                  const venue = [company.exchange || company.mic, company.country].filter(Boolean).join(" · ");
                  return (
                    <Link
                      id={`${listboxId}-${index}`}
                      className={`company-search-option${activeIndex === index ? " active" : ""}`}
                      role="option"
                      aria-selected={activeIndex === index}
                      key={`${company.listing_id || company.issuer_id || company.ticker}-${index}`}
                      to={companyPath(company)}
                      onFocus={() => setActiveIndex(index)}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => setOpen(false)}
                    >
                      <span className="company-search-symbol">{displayTicker(company)}</span>
                      <span className="company-search-name">
                        <b>{company.company_name}</b>
                        <span>{venue || "Listing details unavailable"}</span>
                      </span>
                      <span className="company-search-badges">
                        {company.tier ? <span className="chip blue">{company.tier.replaceAll("_", " ")}</span> : null}
                        <span className={`chip ${company.tracked ? "green" : "gray"}`}>
                          {company.tracked ? "In coverage" : "No coverage"}
                        </span>
                        <span className="document-count">{company.document_count} docs</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="company-lookup-hint">Searches Athena plus the global market directory. Opening a result does not add it to coverage.</div>
    </section>
  );
}

export default function Companies() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const activeQuery = params.get("q") ?? "";
  const tier = params.get("tier") ?? "";
  const [draft, setDraft] = useState(activeQuery);
  useEffect(() => setDraft(activeQuery), [activeQuery]);

  const companies = useInfiniteQuery({
    queryKey: ["companies", activeQuery, tier],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => fetchCompanies({
      q: activeQuery || undefined,
      tier: tier || undefined,
      limit: 50,
      cursor: pageParam,
    }, signal),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    retry: false,
  });
  const firstPage = companies.data?.pages[0];
  const companyRows = useMemo(() => {
    const seen = new Set<string>();
    return (companies.data?.pages ?? []).flatMap((page) => page.companies).filter((company) => {
      if (seen.has(company.research_subject_tier_id)) return false;
      seen.add(company.research_subject_tier_id);
      return true;
    });
  }, [companies.data?.pages]);
  const displayedTotal = firstPage?.total_count
    ?? firstPage?.summary.company_count
    ?? companyRows.length;

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams(params);
    if (draft.trim()) next.set("q", draft.trim()); else next.delete("q");
    setParams(next);
  }

  function setTier(nextTier: string) {
    const next = new URLSearchParams(params);
    if (nextTier) next.set("tier", nextTier); else next.delete("tier");
    setParams(next);
  }

  return (
    <>
      <div className="pagehead">
        <h1>Companies</h1>
        <div className="sub">Find any company, then review its profile, indexed documents, and Athena research coverage.</div>
      </div>
      <CompanyLookup />
      <form className="surface-toolbar company-coverage-toolbar" onSubmit={submit} aria-label="Filter tracked coverage">
        <label>Coverage search<input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Filter ticker or company" aria-label="Filter covered companies" /></label>
        <label>Research tier<select value={tier} onChange={(event) => setTier(event.target.value)} aria-label="Company tier">
          <option value="">All tiers</option><option value="portfolio">Portfolio</option><option value="active_bench">Active bench</option><option value="universe">Universe</option>
        </select></label>
        <button className="btn primary" type="submit">Apply</button>
      </form>
      {companies.isPending ? <div className="empty" role="status">Loading companies…</div> : null}
      {companies.isError && !companies.data ? (
        <div className="error-state company-board-error" role="alert">
          <span>Tracked coverage could not be loaded.</span>
          <button className="btn small" type="button" onClick={() => void companies.refetch()}>Retry</button>
        </div>
      ) : null}
      {firstPage ? (
        <>
          <div className="metric-strip company-metrics">
            <div><b>{firstPage.summary.portfolio_count ?? 0}</b><span>Portfolio</span></div>
            <div><b>{firstPage.summary.active_bench_count ?? 0}</b><span>Active bench</span></div>
            <div><b>{firstPage.summary.universe_count ?? 0}</b><span>Universe</span></div>
          </div>
          <section className="surface-section">
            <div className="section-head company-board-section-head">
              <h2>Tracked coverage</h2>
              <div className="company-board-section-actions">
                <span>{displayedTotal} {displayedTotal === 1 ? "company" : "companies"}</span>
                <button
                  className="btn small"
                  type="button"
                  disabled={companies.isRefetching}
                  onClick={() => void companies.refetch()}
                >
                  {companies.isRefetching ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </div>
            {companies.isRefetchError && !companies.isFetchNextPageError ? (
              <div className="company-board-inline-error" role="alert">
                Athena could not refresh this list. The visible rows may be out of date.
                <button className="btn small" type="button" onClick={() => void companies.refetch()}>Retry refresh</button>
              </div>
            ) : null}
            {companyRows.length ? (
              <div className="table-scroll">
                <table className="data surface-table">
                  <caption className="ops-visually-hidden">Tracked company coverage</caption>
                  <thead><tr><th scope="col">Ticker</th><th scope="col">Company</th><th scope="col">Tier</th><th scope="col">Status</th><th scope="col">Priority</th><th scope="col" className="num">Documents</th><th scope="col">Last document seen</th><th scope="col" className="num">Models</th></tr></thead>
                  <tbody>
                    {companyRows.map((company) => (
                      <tr key={company.research_subject_tier_id}>
                        <td><Link
                          className="ticker"
                          to={companyPath(company)}
                          onFocus={() => {
                            const identifier = companyRef(company);
                            void queryClient.prefetchQuery({
                              queryKey: ["company-workspace", identifier],
                              queryFn: ({ signal }) => fetchCompanyWorkspace(identifier, signal),
                              staleTime: 60_000,
                            });
                          }}
                          onMouseEnter={() => {
                            const identifier = companyRef(company);
                            void queryClient.prefetchQuery({
                              queryKey: ["company-workspace", identifier],
                              queryFn: ({ signal }) => fetchCompanyWorkspace(identifier, signal),
                              staleTime: 60_000,
                            });
                          }}
                        >{company.ticker || "-"}</Link></td>
                        <td>{company.company_name || "-"}</td>
                        <td><span className="chip gray">{company.tier.replaceAll("_", " ")}</span></td>
                        <td><span className={`chip ${company.status === "active" ? "green" : "amber"}`}>{company.status.replaceAll("_", " ")}</span></td>
                        <td>{company.priority}</td>
                        <td className="num">{company.document_count}</td>
                        <td className="nowrap">
                          {company.latest_document_seen_at ? (
                            <time dateTime={company.latest_document_seen_at}>{formatDate(company.latest_document_seen_at)}</time>
                          ) : "—"}
                        </td>
                        <td className="num">{company.tracked_model_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {displayedTotal === 0 ? <div className="empty">No covered companies match these filters.</div> : null}
            {companies.isFetchNextPageError ? (
              <div className="company-board-inline-error" role="alert">
                More covered companies could not be loaded. The visible rows are retained.
                <button className="btn small" type="button" onClick={() => void companies.fetchNextPage()}>Retry load more</button>
              </div>
            ) : null}
            {companies.hasNextPage && !companies.isFetchNextPageError ? (
              <div className="company-board-load-more">
                <button
                  className="btn"
                  type="button"
                  disabled={companies.isFetchingNextPage}
                  onClick={() => void companies.fetchNextPage()}
                >
                  {companies.isFetchingNextPage ? "Loading more…" : "Load more"}
                </button>
                <span>{companyRows.length} of {displayedTotal} shown</span>
              </div>
            ) : null}
          </section>
        </>
      ) : null}
    </>
  );
}
